import { useEffect, useRef, useState } from 'react'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { Loader2Icon } from 'lucide-react'
import { z } from 'zod'

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { authClient } from '@/clients/auth'

const searchSchema = z.object({
  token: z.string().min(1),
  callbackURL: z.string().optional(),
})

export const Route = createFileRoute('/_guest/auth/magic-link')({
  component: MagicLink,
  validateSearch: searchSchema,
})

function MagicLink() {
  const { token, callbackURL } = Route.useSearch()
  const [error, setError] = useState<string | null>(null)
  const verifiedRef = useRef(false)
  const navigate = useNavigate()

  const { data: session, refetch } = authClient.useSession()
  const refetchRef = useRef(refetch)
  refetchRef.current = refetch

  const safeRedirect =
    callbackURL && callbackURL.startsWith('/') ? callbackURL : '/'

  // Navigate when session becomes available
  useEffect(() => {
    if (session?.session) {
      navigate({ to: safeRedirect, replace: true })
    }
  }, [session?.session, navigate, safeRedirect])

  // Verify token on mount
  useEffect(() => {
    if (verifiedRef.current) return
    verifiedRef.current = true

    const verify = async () => {
      const { error: verifyError } = await authClient.magicLink.verify({
        query: { token },
      })

      if (verifyError) {
        // Check if session exists anyway (token already used)
        const { data: existingSession } = await authClient.getSession()
        if (existingSession?.session) {
          refetchRef.current()
          return
        }
        setError(
          verifyError.message ??
            'Link non valido o già usato. Richiedi un nuovo link di accesso.'
        )
        return
      }

      // Success - refetch to update useSession()
      refetchRef.current()
    }

    verify().catch(() => {
      setError('Errore sconosciuto. Richiedi un nuovo link di accesso.')
    })
  }, [token])

  return (
    <div className="container relative flex h-svh flex-col items-center justify-center md:grid lg:max-w-none lg:grid-cols-2 lg:px-0">
      <div className="relative hidden h-full flex-col bg-cyan-200 bg-[url('https://picsum.photos/1600/1000/')] bg-cover bg-center p-10 text-white dark:border-r lg:flex">
        <div className="relative z-20 flex items-center text-lg font-medium">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="mr-2 size-6"
          >
            <path d="M15 6v12a3 3 0 1 0 3-3H6a3 3 0 1 0 3 3V6a3 3 0 1 0-3 3h12a3 3 0 1 0-3-3" />
          </svg>
          Ocarina
        </div>
      </div>
      <div className="h-full lg:p-8">
        <div className="mx-auto flex size-full flex-col items-center justify-center space-y-6 sm:w-[350px]">
          {error ? (
            <>
              <Alert variant="destructive">
                <AlertTitle>Accesso fallito</AlertTitle>
                <AlertDescription>{error}</AlertDescription>
              </Alert>
              <Button asChild variant="outline">
                <Link to="/auth/login">Torna al login</Link>
              </Button>
            </>
          ) : (
            <>
              <Loader2Icon className="size-8 animate-spin text-muted-foreground" />
              <p className="text-sm text-muted-foreground">Accesso in corso…</p>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
